import { ReactElement, useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorMsg from './error-msg/ErrorMsg';
import SectionStep from './section-step/SectionStep';
import { Steps, Space, Button, Form, message, Row, Col, Timeline } from 'antd';
import PreviewModal from './preview-modal/PreviewModal';
import TextContent from 'resources/Text-content.json';
import { Context } from '../../App';
import './configuration.scss';
import Ajv from "ajv";

const YAML = require('json-to-pretty-yaml');


const ajv = new Ajv()
const { Step } = Steps;

interface ISteps {
    title: string;
    content: ReactElement;
}

const Configuration = () => {

    const [steps, setSteps] = useState<ISteps[]>([]);
    const [current, setCurrent] = useState<number>(0);
    const [isError, setError] = useState<boolean>(false);
    const [data, setData] = useState<any>({});
    const [isPreview, setPreview] = useState<boolean>(false);
    const [yamlData, setYamlData] = useState("");
    const [subsectionList, setSubsectionList] = useState<string[][]>([]);
    const [form] = Form.useForm();
    const [config, setConfig] = useState(null);

    const navigate = useNavigate();
    const scrollRef = useRef<any>([]);

    const contextValue = useContext(Context);

    const schemaSectionPattern = "^[a-zA-Z0-9-_ ]+$";
    const schemaSubsectionPattern = "^[a-zA-Z0-9-_ ]+$";

    useEffect(() => {
      if (contextValue?.config && Object.keys(contextValue?.config)?.length) {
        validateConfig(contextValue?.config);
      }
    }, [contextValue?.config]);

    useEffect(() => {
      if (steps?.length) {
        window.addEventListener("scroll", onScrollHandler);
      }
      return () => window.removeEventListener("scroll", onScrollHandler);
    }, [steps]);

    const onScrollHandler = () => {
      console.log("in scroll");
    };

    useEffect(() => {
      if (config) parseConfig(config);
    }, [config]);

    const validateConfig = (config: any) => {
      const propertySchema = {
        type: "array",
        items: {
          type: "object",
          // additionalProperties: false,
          properties: {
            name: {
              type: "string",
            },
            controlType: {
              type: "string",
            },
            options: {
              type: "array",
            },
            required: {
              type: "boolean",
            },
            tooltip: {
              type: "string",
            },
            pattern: {
              type: "string",
            },
            placeholder: {
              type: "string",
            },
          },
          required: ["name", "controlType"],
        },
      };
      const subsectionSchema = {
        type: "object",
        additionalProperties: false,
        patternProperties: {
          [schemaSubsectionPattern]: {
            type: "object",
            additionalProperties: false,
            properties: {
              props: propertySchema,
            },
            required: ["props"],
          },
        },
      };
      const sectionSchema = {
        type: "object",
        additionalProperties: false,
        patternProperties: {
          [schemaSectionPattern]: {
            type: "object",
            additionalProperties: false,
            properties: {
              subsections: subsectionSchema,
            },
            required: ["subsections"],
          },
        },
      };
      const schema = {
        type: "object",
        additionalProperties: false,
        properties: {
          sections: sectionSchema,
        },
        required: ["sections"],
      };
      const validate = ajv.compile(schema);
      const valid = validate(config);
      if (!valid) {
        setError(true);
      }
      setScrollViewer(config);
      parseConfig(config);
    };

    const setScrollViewer = (config: any) => {
      try {
        const sections: any = config?.sections;
        let list = [];
        for (const key in sections) {
          let temp = [];
          for (const k in sections[key]?.subsections) {
            temp.push(k);
          }
          list.push(temp);
        }
        setSubsectionList(list);
      } catch (e) {
        setError(true);
      }
    };

    const parseConfig = (config: any) => {
      try {
        let stepsArray = [],
          i = 0;
        const sections: any = config?.sections;
        if (!Object.keys(sections)?.length) {
          // regex pattern in schema for sections does not make it required in ajv. Hence validation
          setError(true);
          return;
        }
        for (const section of Object.entries(sections)) {
          const [sectionTitle, subsections]: any = section;
          if (!subsections || !Object.keys(subsections?.subsections)?.length) {
            // regex pattern in schema for subsections does not make it required in ajv. Hence validation
            setError(true);
            return;
          }
          const content = (
            <SectionStep
              key={sectionTitle}
              sectionTitle={sectionTitle}
              subsections={subsections?.subsections}
              onChange={onChange}
              form={form}
              scrollRef={scrollRef}
              sectionIndex={i}
            />
          );
          stepsArray.push({
            title:
              sectionTitle?.charAt(0)?.toUpperCase() + sectionTitle?.slice(1),
            content: content,
          });
          i++;
        }
        setSteps(stepsArray);
      } catch (e) {
        setError(true);
      }
    };

    const prev = () => {
      setCurrent(current - 1);
      window.scrollTo({ top: 0 });
    };

    const next = async () => {
      await form
        .validateFields()
        .then(() => {
          setCurrent(current + 1);
          window.scrollTo({ top: 0 });
        })
        .catch(() => {
          message.warning(TextContent?.validation);
        });
    };

    const onCancel = () => {
      setPreview(false);
    };

    const executeActionHandler = (
      options: any,
      name: string,
      value: any,
      section: string,
      subsection: string
    ) => {
      //   const trying = actionHandler ? actionHandler(value) : "";

      const selectedOption = options?.find(
        (option: any) => option.value === value
      );

      let updatedData = data;

      let updatedConfig = contextValue?.config;

      let updateRequired = false;
      const subSectionProps =
        updatedConfig.sections[section].subsections[subsection].props;
      let updatedProps = [];

      updatedProps = subSectionProps.map((prop: any) => {
        if (prop.name === name && prop.selectedValue !== selectedOption.value) {
          updateRequired = true;
          return { ...prop, selectedValue: selectedOption.value };
        }
        const hidePropFound = selectedOption?.hiddenElements?.find(
          (item: string) => prop.name === item
        );
        hidePropFound && delete updatedData[section][subsection][prop.name];
        hidePropFound && !prop.hidden && (updateRequired = true);
        hidePropFound &&
          form.setFieldValue(`${section}${subsection}${prop.name}`, "");
        return hidePropFound
          ? { ...prop, selectedValue: "", hidden: true }
          : { ...prop };
      });

      updatedProps = updatedProps.map((prop: any) => {
        const showPropFound = selectedOption?.showElements?.find(
          (item: string) => prop.name === item
        );
        showPropFound && prop.hidden && (updateRequired = true);
        return showPropFound ? { ...prop, hidden: false } : { ...prop };
      });

      updatedConfig.sections[section].subsections[subsection].props =
        updatedProps;
      contextValue.config = updatedConfig;

      setData({ ...updatedData });
      updateRequired && setConfig({ ...updatedConfig });
    };

    const onChange = (
      section: string,
      subsection: string,
      name: string,
      value: any,
      options?: any
    ) => {
      // console.log('section',section)
      // console.log('subsection',subsection)
      // console.log('name',name)
      // console.log('value',value)
      let newdata = data;
      // console.log('data',data)
      if (newdata[section]) {
        if (newdata[section][subsection]) {
          newdata[section][subsection][name] = value;
        } else {
          newdata[section][subsection] = {};
          newdata[section][subsection][name] = value;
        }
      } else {
        newdata[section] = {};
        newdata[section][subsection] = {};
        newdata[section][subsection][name] = value;
      }
      // console.log('newdata',newdata)
      options?.length &&
        executeActionHandler(options, name, value, section, subsection);
      setData(newdata);
    };

    const onSubmit = async () => {
      await form
        .validateFields()
        .then(() => {
          generateYaml();
        })
        .catch(() => {
          message.warning("Please complete all details to proceed!");
        });
    };

    const generateYaml = () => {
      const yaml = YAML.stringify({
        install_type: contextValue.installType,
        services: data,
      });
      // console.log(yaml);
      setYamlData(yaml);
      setPreview(true);
    };

    const download = () => {
      const element = document.createElement("a");
      element.setAttribute(
        "href",
        "data:text/plain;charset=utf-8," + encodeURIComponent(yamlData)
      );
      element.setAttribute("download", "deployment.yaml");
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    };

    const scrollToSubsection = (section: number, subsection: number) => {
      // console.log('scrollref', scrollRef.current);
      // console.log('scrollref section', scrollRef.current?.[section]);
      // console.log('scrollref subsection', scrollRef.current?.[section]?.[subsection]);
      // console.log('scrollref offset', scrollRef.current?.[section]?.[subsection]?.offsetTop);
      window.scrollTo({
        top: scrollRef.current?.[section]?.[subsection]?.offsetTop,
        behavior: "smooth",
      });
    };

    return (
        <div className="config-wrapper">
            {
                !isError ? (steps?.length &&
                    (<>
                        <Row>
                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }}>
                                <div className="scroll-container">
                                    <Timeline >
                                        {subsectionList && subsectionList[current]?.map((val, index) => {
                                            return (
                                                <Timeline.Item className="scroll-item" color="#CACACC">
                                                    <div onClick={() => scrollToSubsection(current, index)}>
                                                        {val?.charAt(0)?.toUpperCase() + val?.slice(1)}
                                                    </div>
                                                </Timeline.Item>
                                            );
                                        })}
                                    </Timeline>
                                </div>
                            </Col>
                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 16 }} lg={{ span: 16 }} xl={{ span: 16 }}>
                                <div className="step-container">
                                    <Space direction="vertical" size="large">
                                        <Steps current={current} responsive={true} type="navigation">
                                            {steps.map((item, index) => (
                                                <Step key={index} title={item?.title} />
                                            ))}
                                        </Steps>
                                        <div>{steps[current]?.content}</div>
                                        <div>
                                            <Space size="middle">
                                                {current > 0 && (
                                                    <Button onClick={() => prev()} className="btn-transparent">
                                                        {TextContent?.btn_previous}
                                                    </Button>
                                                )}
                                                <Button onClick={() => navigate('/home')} className="btn-transparent">
                                                    {TextContent?.btn_cancel}
                                                </Button>
                                                {current < steps.length - 1 && (
                                                    <Button
                                                        type="primary"
                                                        onClick={() => next()}
                                                    >
                                                         {TextContent?.btn_next}
                                                    </Button>
                                                )}
                                                {current === steps.length - 1 && (
                                                    <Button
                                                        type="primary"
                                                        onClick={() => onSubmit()}
                                                    >
                                                         {TextContent?.btn_generate}
                                                    </Button>
                                                )}
                                            </Space>
                                        </div>
                                    </Space>
                                </div>
                            </Col>
                        </Row>
                    </>)) : <ErrorMsg />
            }
            {
                isPreview && <PreviewModal
                    visible={isPreview}
                    onCancel={onCancel}
                    data={yamlData}
                    download={download}
                />
            }
        </div >
    )
}

export default Configuration;